<template>
    <label class="form-group big" :class="{ disabled: disabled }">
        <div class="control-label" v-if="resourceKey">
            <span v-html="ui.t(resourceKey)"></span>
        </div>

        <input type="text" :step="step" :min="min" :max="max"
                v-model.number="inputValue"
                class="form-control" :class="inputClasses"
                ref="kissa2"
                v-on:keyup.enter="blur"
                @blur="blur"
                @keydown="keydown"
                @focus="focus"
                :disabled="disabled" />

        <span class="input-tip" v-if="valueWasChanged" v-html="ui.t('valueChanged')"></span>
    </label>
</template>

<script async setup lang="ts">
import { nextTick, ref, toRef, useSSRContext, watch, type Ref } from 'vue';
import { useState, useUI } from '../store';


const {
    resourceKey = "",
    classes,
    modelValue,
    step,
    max,
    min = 0,
    disabled = false
} = defineProps<{
    resourceKey: string,
    classes: string[] | string,
    modelValue: number | null,
    step: number,
    max: number,
    min: number, 
    disabled: boolean
}>();

const emit = defineEmits(["input", "focus", "blur", "update:modelValue"]);

const { state } = useState();
const { ui } = useUI();

const valueWasChanged = ref(false);
const inputValue: Ref<number | null> = ref(modelValue);
const hasFocus = ref(false);

let keyUpTimer: number | null = null;

function created() {
    if (isNaN(inputValue.value!)) {
        inputValue.value = null;
    }
}

watch(() => modelValue, (newValue) => {
    if (resourceKey && ui.lastFocusedField === resourceKey && newValue != inputValue.value && !valueWasChanged.value) {
        valueWasChanged.value = true;
        setTimeout(() => valueWasChanged.value = false, 4000);
    }
    inputValue.value = newValue
});

function focus() {
    hasFocus.value = true;
    emit('focus'); 
}

function keydown(event: KeyboardEvent) {
    if (event.key.length == 1 && isNaN(parseInt(event.key))) {
        event.preventDefault();

        return;
    }

    clearTimeout(keyUpTimer!);

    keyUpTimer = setTimeout(() => ensureValue(), 2000);
}

function blur() {
    hasFocus.value = false;

    ensureValue();

    emit('blur');
}

function inputClasses() {
    let classes: string[] = [];
    let paramClasses = classes || [];

    if (hasFocus)
        classes.push("focus");

    return [ ...classes, ...paramClasses ];
}

function ensureValue() {
    if (!inputValue.value || inputValue.value.toString() == "") {
        return;
    }

    let value = getValidValue(inputValue.value, true, true);

    let valueChanged = value !== inputValue.value;

    if (valueChanged) {
        valueWasChanged.value = true;

        setTimeout(function() {
            valueWasChanged.value = false;
        }, 4000);
    }

    emit("update:modelValue", value);

    nextTick(() => {
        state.dirty = true;
        
        if (resourceKey && ui.lastFocusedField === resourceKey && modelValue != inputValue.value) {
            valueWasChanged.value = true;
            inputValue.value = modelValue;
            setTimeout(() => valueWasChanged.value = false, 4000);
        }
    });
    //     if (typeof value == "string") {
    //         inputValue.value = null;
    //     } else {
    //         inputValue.value = value;
    //     }
    // });
}

function getValidValue(newValue: number | string, validateMin: boolean, validateMax: boolean) {
    let realStep = step || 1;
    let number = Math.ceil(Number(newValue) / realStep) * realStep;

    if (max && number > max  && validateMax)
        return max;

    if (newValue == "" && min < 0 && validateMin)
        return newValue;

    if (number <= min && validateMin)
        return min;

    return number;
}

created();

</script>